'use client';

import { useState } from 'react';
import { useTranslations } from '@/i18n/context';

type FaqItem = {
  q: string;
  a: string;
};

export function Faq() {
  const t = useTranslations('faq');
  const items = t.raw('items') as FaqItem[];
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id="faq"
      className="relative border-t hairline bg-[color:var(--mp-cream-soft)] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
        <div className="mb-16 grid grid-cols-1 gap-10 md:mb-20 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="mb-4 text-[11px] uppercase tracking-[0.32em] text-[color:var(--mp-moss)]">
              {t('sectionLabel')}
            </div>
            <h2
              className="font-serif-cn text-4xl leading-[1.2] text-[color:var(--mp-forest)] md:text-5xl"
              // eslint-disable-next-line react/no-danger -- trusted translation
              dangerouslySetInnerHTML={{ __html: t('title') }}
            />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {items.map((item, i) => {
              const open = openIndex === i;
              return (
                <div key={item.q} className="border-t hairline">
                  <button
                    type="button"
                    onClick={() => setOpenIndex(open ? null : i)}
                    className="flex w-full items-start justify-between gap-6 py-6 text-left md:py-8"
                  >
                    <div className="flex items-baseline gap-5">
                      <span className="font-display text-sm italic text-[color:var(--mp-caramel)]">
                        {(i + 1).toString().padStart(2, '0')}
                      </span>
                      <span className="font-serif-cn text-lg leading-[1.5] text-[color:var(--mp-forest)] md:text-xl">
                        {item.q}
                      </span>
                    </div>
                    <span
                      aria-hidden
                      className={`mt-2 inline-block h-[1px] w-5 shrink-0 bg-[color:var(--mp-forest)] transition-transform duration-500 ${
                        open ? 'rotate-0' : 'rotate-90'
                      }`}
                    />
                  </button>
                  <div
                    className={`grid overflow-hidden transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                      open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="min-h-0">
                      <p className="pb-8 pl-10 pr-6 text-[15px] leading-[1.95] text-[color:var(--mp-forest)]/75 md:pl-12">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="border-t hairline" />
          </div>
        </div>
      </div>
    </section>
  );
}
